/**
 * Logger utility for Qwixx Online
 * Automatically enables logs in development and disables in production
 * Can be forcefully enabled in production with DEBUG=true environment variable
 */

// Check if we're in development environment or debug mode is enabled
const isDevelopment = process.env.NODE_ENV === 'development';
const isDebugEnabled = process.env.REACT_APP_DEBUG_LOGGING === 'true';

// Log levels
const LOG_LEVELS = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
};

// Only log if we're in development mode or debug is explicitly enabled
const shouldLog = isDevelopment || isDebugEnabled;

/**
 * Generic log function that handles all logging levels
 * @param {string} level - Log level (debug, info, warn, error)
 * @param {string} message - The message to log
 * @param {any} data - Optional data to include in the log
 */
const log = (level, message, data) => {
  // Skip all DEBUG logs
  if (level === LOG_LEVELS.DEBUG) return;
  
  // Skip logging in production unless debug is enabled
  if (!shouldLog && level === LOG_LEVELS.INFO) return;
  
  const timestamp = new Date().toISOString();
  const prefix = `[${timestamp}] [${Object.keys(LOG_LEVELS)[level]}]`;
  
  switch (level) {
    case LOG_LEVELS.DEBUG:
      // This code will never run but kept for completeness
      if (data) {
        console.debug(`${prefix} ${message}`, data);
      } else {
        console.debug(`${prefix} ${message}`);
      }
      break;
    case LOG_LEVELS.INFO:
      if (data) {
        console.info(`${prefix} ${message}`, data);
      } else {
        console.info(`${prefix} ${message}`);
      }
      break;
    case LOG_LEVELS.WARN:
      if (data) {
        console.warn(`${prefix} ${message}`, data);
      } else {
        console.warn(`${prefix} ${message}`);
      }
      break;
    case LOG_LEVELS.ERROR:
      if (data) {
        console.error(`${prefix} ${message}`, data);
      } else {
        console.error(`${prefix} ${message}`);
      }
      break;
    default:
      if (data) {
        console.log(`${prefix} ${message}`, data);
      } else {
        console.log(`${prefix} ${message}`);
      }
  }
};

// Exported logger functions
const logger = {
  debug: (message, data) => {
    // DEBUG logs are completely disabled
    return;
  },
  info: (message, data) => log(LOG_LEVELS.INFO, message, data),
  warn: (message, data) => log(LOG_LEVELS.WARN, message, data),
  error: (message, data) => log(LOG_LEVELS.ERROR, message, data),
  
  // Utility method to check if debug logging is enabled
  isDebugEnabled: () => false,
};

export default logger; 